/**
 * DecisionFlow — AgentGovernance
 * ui/src/components/platform-admin/AgentGovernance.jsx
 * S1034 — Platform Agent Governance
 *
 * New page in platform admin shell at /platform-admin/agent-governance.
 * RBAC: platform_admin and tool_admin.
 * spec_agent_operating_mode_s1033.md §7
 * DECISION-S1033-08: closes platform admin kill switch UI gap.
 *
 * Endpoints:
 *   POST /api/v1/admin/platform-agent/kill-switch — §7.4 platform-wide kill switch
 *   GET  /api/v1/admin/platform-agent/status      — §7.3 tenant agent status (read-only)
 *
 * Privacy rules (confirmed safe per mastercontext S1034):
 *   Surfaces governance metadata only — current_mode, kill switch state, session count,
 *   model, last_changed_at, last_changed_by_role. No compensation data. No session content.
 *   No PII. No position data. No payout amounts. No tenant kill switch reasons
 *   (those are tenant-operational context, not platform admin domain).
 *   Consistent with NN-40 and L2.8.
 *
 * Design rules (match platform-admin/*.jsx pattern):
 *   rgb(var(--color-*)) throughout — no hardcoded hex
 *   text-tok-* sizes
 *   .skeleton for loading states
 *   Tables: max-h + overflow-y-auto + sticky thead
 *   Modal does NOT close on outside click (NN-07)
 */

import { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";

const API_KS     = "/api/v1/admin/platform-agent/kill-switch";
const API_STATUS = "/api/v1/admin/platform-agent/status";
const REASON_MIN = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 py-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-10 w-full rounded skeleton" />
      ))}
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data."}
    </div>
  );
}

const MODE_LABELS = {
  T0: "T0 — Off",
  T1: "T1 — Advise",
  T2: "T2 — Full",
};

function ModePill({ mode }) {
  const tones = {
    T0: "text-[rgb(var(--color-danger))] bg-[rgb(var(--color-danger))]/8 border-[rgb(var(--color-danger))]/30",
    T1: "text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/8 border-[rgb(var(--color-warning))]/30",
    T2: "text-[rgb(var(--color-success))] bg-[rgb(var(--color-success))]/8 border-[rgb(var(--color-success))]/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-tok-xs font-bold ${tones[mode] ?? tones.T2}`}>
      {MODE_LABELS[mode] ?? mode}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Platform Kill Switch Banner
// ---------------------------------------------------------------------------

function PlatformKillSwitchBanner({ status }) {
  if (!status) return null;

  if (status.active) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-5 py-5">
        <div className="flex items-start gap-4">
          <ShieldOff className="mt-0.5 h-6 w-6 shrink-0 text-[rgb(var(--color-danger))]" />
          <div className="flex-1 min-w-0">
            <p className="text-tok-base font-bold text-[rgb(var(--color-danger))]">
              PLATFORM AGENT HALTED — all tenant AI activity suspended
            </p>
            <div className="mt-2 space-y-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              <p>Activated: <span className="text-[rgb(var(--color-text))]">{formatDate(status.activated_at)}</span></p>
              <p>Activated by: <span className="text-[rgb(var(--color-text))]">{status.activated_by ?? "—"}</span></p>
              {status.reason && (
                <p>Reason: <span className="text-[rgb(var(--color-text))]">{status.reason}</span></p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-5 py-4">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
        <div>
          <p className="text-tok-sm font-bold text-[rgb(var(--color-success))]">
            All tenants operating normally — no platform restrictions active
          </p>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Platform kill switch inactive. Individual tenant modes apply.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kill Switch Action Modal
// ---------------------------------------------------------------------------

function KillSwitchModal({ action, onConfirm, onCancel, saving }) {
  const [reason, setReason] = useState("");
  const isActivate = action === "activate";
  const canSubmit  = reason.trim().length >= REASON_MIN && !saving;

  const tone = isActivate
    ? "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/8"
    : "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/8";

  const btnCls = isActivate
    ? "bg-[rgb(var(--color-danger))] hover:opacity-90 text-white"
    : "bg-[rgb(var(--color-success))] hover:opacity-90 text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">

        <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${tone}`}>
          {isActivate
            ? <ShieldOff  className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-danger))]" />
            : <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
          }
          <div>
            <p className="text-tok-sm font-bold text-[rgb(var(--color-text))]">
              {isActivate
                ? "Activate platform kill switch — halt ALL tenants?"
                : "Deactivate platform kill switch — resume ALL tenants?"
              }
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {isActivate
                ? "This immediately suspends AI agent activity for every tenant on the platform. Use only for platform-wide safety events."
                : "All tenants will resume their configured operating mode immediately."
              }
            </p>
          </div>
        </div>

        {isActivate && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
              This affects ALL tenants. This is a platform-wide action.
            </p>
          </div>
        )}

        <div className="mb-4 space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isActivate
                ? "e.g. Platform security event — halting all AI activity pending review…"
                : "e.g. Security event resolved — resuming normal AI operations…"
            }
            disabled={saving}
            className="w-full resize-none rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
          />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {reason.trim().length}/{REASON_MIN} characters minimum · stored in audit trail
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            className={`rounded-xl px-4 py-2 text-tok-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${btnCls}`}
          >
            {saving
              ? (isActivate ? "Activating…" : "Deactivating…")
              : (isActivate ? "Activate Platform Kill Switch" : "Deactivate Platform Kill Switch")
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tenant Status Table — §7.3 read-only
// ---------------------------------------------------------------------------

function TenantStatusTable({ tenants, loading, error }) {
  if (loading) return <LoadingSkeleton />;
  if (error)   return <ErrorNote message={error} />;
  if (!tenants || tenants.length === 0) {
    return <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No tenant data available.</p>;
  }

  return (
    <div className="max-h-[calc(100vh-480px)] min-h-[240px] overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
      <table className="w-full text-tok-sm">
        <thead className="sticky top-0 z-10 bg-[rgb(var(--color-surface-muted))]">
          <tr>
            {["Tenant", "Mode", "Kill Switch", "Last Changed", "Role", "Model"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[rgb(var(--color-border))]">
          {tenants.map((t) => (
            <tr key={t.tenant_id} className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))] transition">
              <td className="px-3 py-2.5">
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{t.tenant_name}</p>
              </td>
              <td className="px-3 py-2.5">
                <ModePill mode={t.current_mode ?? "T2"} />
              </td>
              <td className="px-3 py-2.5">
                {t.kill_switch_active ? (
                  <span className="inline-flex items-center gap-1 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
                    <ShieldOff className="h-3.5 w-3.5" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Clear
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                {t.last_changed_at ? formatDate(t.last_changed_at) : "—"}
              </td>
              <td className="px-3 py-2.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {t.last_changed_by_role ?? "—"}
              </td>
              <td className="px-3 py-2.5 text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">
                {t.current_mode === "T0" ? "—" : (t.model_version ?? "—")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AgentGovernance root
// ---------------------------------------------------------------------------

export default function AgentGovernance() {
  const [platformKs, setPlatformKs] = useState(null);
  const [tenants,    setTenants]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [modal,      setModal]      = useState(null);  // 'activate' | 'deactivate' | null
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(API_STATUS, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `HTTP ${res.status}`);
      setPlatformKs(body.platform_kill_switch ?? null);
      setTenants(body.tenants ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleKillSwitch(reason) {
    const action = platformKs?.active ? "deactivate" : "activate";
    setSaving(true);
    try {
      const res = await fetch(API_KS, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, reason }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error?.message ?? `Error ${res.status}`);
      setToast(body.message ?? (action === "activate"
        ? "Platform kill switch activated. All tenants halted."
        : "Platform kill switch deactivated. All tenants resuming."));
      setModal(null);
      await fetchStatus();
    } catch (e) {
      setToast(`Error: ${e.message}`);
      setModal(null);
    } finally {
      setSaving(false);
    }
  }

  const isActive = platformKs?.active ?? false;

  return (
    <div className="space-y-4">

      {/* Toast */}
      {toast && (
        <div className="fixed right-5 top-5 z-50 max-w-sm rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 shadow-lg text-tok-sm text-[rgb(var(--color-text))]">
          {toast}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <KillSwitchModal
          action={modal}
          onConfirm={handleKillSwitch}
          onCancel={() => setModal(null)}
          saving={saving}
        />
      )}

      {/* Section 1: Platform Kill Switch */}
      <Card
        title="Platform Kill Switch"
        action={
          <Button variant="secondary" onClick={fetchStatus} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      >
        <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
          The platform kill switch halts AI agent activity for <strong>all tenants</strong> immediately.
          Use only for platform-wide safety events. Individual tenant kill switches are operated by
          each tenant's Super Admin.
        </p>

        {loading && <LoadingSkeleton />}
        {error && !loading && <ErrorNote message={error} />}

        {!loading && !error && (
          <div className="space-y-4">
            <PlatformKillSwitchBanner status={platformKs} />

            <div>
              {isActive ? (
                <button
                  type="button"
                  onClick={() => setModal("deactivate")}
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-5 py-3 text-tok-sm font-bold text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/15 transition-colors"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Deactivate Platform Kill Switch — Resume All Tenants
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setModal("activate")}
                  className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-5 py-3 text-tok-sm font-bold text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/15 transition-colors"
                >
                  <ShieldAlert className="h-4 w-4" />
                  Activate Platform Kill Switch — Halt All Tenants
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Section 2: Tenant Agent Status — read-only */}
      <Card title="Tenant Agent Status">
        <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Read-only view of per-tenant agent governance state.
          Tenant mode governance is managed by each tenant's Super Admin.
          Showing: operating mode, kill switch state, last change time and role, model.
          No compensation data, session content, or PII is shown.
        </p>
        <TenantStatusTable
          tenants={tenants}
          loading={loading}
          error={error}
        />
        {!loading && tenants && tenants.length > 0 && (
          <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {tenants.length} active {tenants.length === 1 ? "tenant" : "tenants"}.
            Kill switch states shown are tenant-level — independent of the platform kill switch above.
          </p>
        )}
      </Card>
    </div>
  );
}
